import { createContext, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Toast } from "@base-ui/react/toast";
import { Tooltip } from "@base-ui/react/tooltip";
import { Toaster } from "../ui/toast.jsx";
import "./tokens.css";
import "./base.css";

/**
 * Diotia theme layer on top of Base UI.
 *
 * Base UI ships no styles; every part is styled through the `dt-*` classes in `ui/components.css`,
 * which resolve exclusively to the CSS custom properties declared in `tokens.css`. A theme is
 * therefore just a set of token overrides plus a color mode.
 */

export type ThemeMode = "system" | "light" | "dark";

/** Token overrides. Keys are the token names without the `--dt-` prefix (e.g. `accent`). */
export type ThemeTokens = Partial<{
  accent: string;
  accentHover: string;
  accentActive: string;
  accentFg: string;
  accentSoft: string;
  accentSoftStrong: string;
  bg: string;
  surface: string;
  surface2: string;
  surface3: string;
  border: string;
  borderStrong: string;
  fg: string;
  fg2: string;
  fg3: string;
  font: string;
  fontMono: string;
  radius: string;
  radiusSm: string;
  radiusLg: string;
  sidebarW: string;
  layoutWide: string;
  layoutNormal: string;
}>;

export interface Theme {
  /** CSS custom properties applied to the app root. */
  vars: CSSProperties;
  /** Initial color mode when nothing is stored for the user. */
  defaultMode: ThemeMode;
}

const kebab = (k: string) => k.replace(/([a-z0-9])([A-Z0-9])/g, "$1-$2").toLowerCase();

export function createTheme(tokens: ThemeTokens = {}, options: { defaultMode?: ThemeMode } = {}): Theme {
  const vars: Record<string, string> = {};
  for (const [k, v] of Object.entries(tokens)) if (v !== undefined) vars[`--dt-${kebab(k)}`] = v;
  return { vars: vars as CSSProperties, defaultMode: options.defaultMode ?? "system" };
}

/** The stock Diotia theme: indigo accent on warm neutrals, follows the OS color scheme. */
export const diotiaTheme = createTheme();

const STORAGE_KEY = "diotia.theme";

function readStoredMode(): ThemeMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : null;
  } catch {
    return null;
  }
}

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}

export function ThemeProvider({ theme = diotiaTheme, children }: { theme?: Theme; children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode() ?? theme.defaultMode);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", mode);
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      setMode: (next) => {
        setModeState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          /* per-viewer convenience only */
        }
      },
    }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <Tooltip.Provider delay={400}>
        <Toast.Provider timeout={5000}>
          <div className="dt-root" style={theme.vars}>
            {children}
          </div>
          <Toaster />
        </Toast.Provider>
      </Tooltip.Provider>
    </ThemeContext.Provider>
  );
}
